import {EventEmitter, inject, Injectable} from '@angular/core';
import {TaskStore} from '../../stores/task.store';
import {UserTasksService as ApiUserTasksService} from '../../api/services/user-tasks.service';
import {AuthQuery} from '../../queries/auth.query';
import {VaultQuery} from '../../queries/vault.query';
import {filter, map, repeat, skipWhile, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {VaultsService as ApiVaultsService} from '../../api/services/vaults.service';
import {combineLatest, firstValueFrom, interval, Subscription, timer} from 'rxjs';
import {UserTask} from 'src/app/api/models/user-task';
import {Router} from '@angular/router';
import {AppQuery} from '../../queries/app.query';
import {UserMagnetTask} from '../../api/models/user-magnet-task';
import {TaskQuery} from '../../queries/task.query';
import {IsLoggedIn} from '../../decorators/is-logged-in.decorator';
import {Vault} from '../../api/models/vault';
import {AuthService} from '../auth/auth.service';

@Injectable({
    providedIn: 'root'
})
export class TaskService {
    private readonly appQuery = inject(AppQuery);
    private readonly authQuery = inject(AuthQuery);
    private readonly taskStore = inject(TaskStore);
    private readonly vaultQuery = inject(VaultQuery);
    private readonly apiUserTasksService = inject(ApiUserTasksService);
    private readonly apiVaultsService = inject(ApiVaultsService);
    private readonly router = inject(Router);
    private readonly taskQuery = inject(TaskQuery);
    private readonly authService = inject(AuthService);
    private readonly fetchedData$: EventEmitter<void>;

    public constructor() {
        this.fetchedData$ = new EventEmitter<void>();
        this.initOpenTaskPolling();
    }

    public setActiveTaskById(taskVaultId: string, magnetId?: string): void {
        this.taskStore.setActive([taskVaultId]);
        if (magnetId) {
            this.taskStore.update({ selectedMagnetTask: magnetId });
        } else {
            const storeValue = this.taskStore.getValue();
            if (storeValue && !storeValue.selectedMagnetTask && storeValue.entities && storeValue.entities[taskVaultId]) {
                if (storeValue.entities[taskVaultId].userTask.magnetTasks.length > 0) {
                    this.taskStore.update({ selectedMagnetTask: storeValue.entities[taskVaultId].userTask.magnetTasks[0].id });
                }
            }
        }
    }

    public setSelectedMagnetTask(userMagnetTaskId: string | undefined): void {
        this.taskStore.update({ selectedMagnetTask: userMagnetTaskId });
    }

    public async checkForOpenTasks(): Promise<boolean> {
        await this.fetchUserOrVaultTasks();
        return this.taskQuery.hasTasks();
    }

    public async fetchGlobalUserTasks(): Promise<void> {
        this.taskStore.setLoading(true);
        try {
            const sortedUserTasks: Array<UserTask> =
                await firstValueFrom(this.apiUserTasksService.UserTasksGetAll(await this.authService.getBearer())
                    .pipe(map(
                        userTasks => userTasks
                            .filter(task => this.vaultQuery.getIsVaultInStoreById(task.vaultId))
                            .sort((firstUserTask: UserTask, secondUserTask: UserTask) => {
                                const firstVault = this.vaultQuery.getVaultById(firstUserTask?.vaultId) as Vault;
                                const secondVault = this.vaultQuery.getVaultById(secondUserTask?.vaultId) as Vault;

                                return firstVault.name.localeCompare(secondVault.name);
                            }))));
            for (const userTask of sortedUserTasks) {
                userTask.magnetTasks.sort((firstMagnetTask: UserMagnetTask, secondMagnetTask: UserMagnetTask) =>
                    firstMagnetTask.name.localeCompare(secondMagnetTask.name)
                );
            }
            for (const userTask of sortedUserTasks) {
                this.processDocumentCount(userTask);
            }
            const filteredUserTasks = sortedUserTasks.filter(task => task.totalDocumentCount > 0);

            this.taskStore.set(sortedUserTasks.map(userTask => ({
                id: userTask.vaultId,
                userTask
            })));
            this.taskStore.update({ hasOpenTasks: filteredUserTasks.length > 0 });
        } catch (e) {
            this.resetUserTasks();
            console.error(e);
        } finally {
            this.taskStore.setLoading(false);
        }
    }

    public async fetchVaultUserTasks(vaultId: string): Promise<void> {
        await this.vaultQuery.waitForHasDataLoaded();
        this.taskStore.setLoading(true);
        try {
            const vaultUserTask = await firstValueFrom(this.apiVaultsService.VaultsGetVaultUserTasks({
                vaultId,
                Authorization: await this.authService.getBearer()
            }));

            if (vaultUserTask) {
                this.processDocumentCount(vaultUserTask);

                vaultUserTask.magnetTasks.sort((firstMagnetTask: UserMagnetTask, secondMagnetTask: UserMagnetTask) =>
                    firstMagnetTask.name.localeCompare(secondMagnetTask.name)
                );

                if (vaultUserTask.totalDocumentCount > 0 || vaultUserTask.trashedDocumentCount > 0) {
                    this.taskStore.upsert(vaultId,
                        {
                            id: vaultId,
                            userTask: vaultUserTask
                        });
                } else {
                    this.taskStore.remove(vaultId);
                }
            } else {
                this.taskStore.remove(vaultId);
            }
        } catch (e) {
            console.error(e);
        } finally {
            this.taskStore.setLoading(false);
        }
    }

    public resetTimer(): void {
        this.fetchedData$.emit();
    }

    public intervalPollingForOpenTasks(amountOfEmissions: number, intervalTimeInMs: number = 5000): void {
        const polling = interval(intervalTimeInMs)
            .pipe(take(amountOfEmissions));

        polling.subscribe(() => {
            this.fetchedData$.emit();
        });
    }

    private async fetchUserOrVaultTasks(): Promise<void> {
        const isSearchTaskGlobalScope = await firstValueFrom(this.appQuery.isSearchTaskGlobalScope$.pipe(take(1)));

        if (isSearchTaskGlobalScope) {
            await this.fetchGlobalUserTasks();
        } else {
            const vaultId = this.vaultQuery.getFirstActiveId();
            if (vaultId) {
                await this.fetchVaultUserTasks(vaultId);
            }
        }
    }

    private resetUserTasks(): void {
        this.taskStore.reset();
    }

    private processDocumentCount(userTask: UserTask): void {
        userTask.totalDocumentCount -= userTask.trashedDocumentCount;
        userTask.totalDocumentCount -= userTask.classificationDocumentCount;
    }

    @IsLoggedIn()
    private initOpenTaskPolling(): void {
        const subscriptions = new Subscription();
        const pollingIntervalInMs = 1000 * 5 * 60; // check for tasks every 5 minutes
        let amountOfEmissions = 0;
        const fetchData$ = this.fetchedData$.pipe(tap(() => {
            amountOfEmissions = 0;
        }));
        const timer$ = timer(0, pollingIntervalInMs)
            .pipe(
                takeUntil(fetchData$),
                repeat()
            );
        subscriptions.add(this.authQuery.isLoggedIn$.pipe(
                skipWhile(isLoggedIn => !isLoggedIn), // wait for logged in
                switchMap(() =>
                    combineLatest([
                        this.vaultQuery.waitForHasDataLoaded(),
                        timer$
                    ])), // when logged in start interval, but also wait for vault store has data
                filter(() => {
                    return amountOfEmissions === 0 || !this.router.url.includes('tasks');
                }), // exit when on tasks, except it is the first check
            )
            .subscribe(() => {
                amountOfEmissions++;
                return this.checkForOpenTasks();
            }));
        subscriptions.add(this.authQuery.isLoggedIn$.subscribe((isLoggedIn) => {
            if (!isLoggedIn) {
                subscriptions.unsubscribe();
            }
        }));
    }
}
